/**
 * Yandex.Metrika counter. Loaded only on public marketing pages.
 * Set VITE_YANDEX_METRIKA_ID at build time (see frontend/.env.example).
 */

const SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js'
const NOSCRIPT_ID = 'ym-noscript-pixel'

declare global {
  interface Window {
    ym?: ((...args: any[]) => void) & { a?: any[]; l?: number }
  }
}

export function getYandexMetrikaId(): number | null {
  const raw = import.meta.env.VITE_YANDEX_METRIKA_ID?.trim()
  if (!raw) return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function isTestHost(): boolean {
  if (typeof window === 'undefined') return false
  const host = window.location.hostname.toLowerCase()
  return host.startsWith('test.')
}

function injectLoaderScript(counterId: number): void {
  const scripts = Array.from(document.getElementsByTagName('script'))
  if (scripts.some((s) => s.src === `${SCRIPT_SRC}?id=${counterId}` || s.src === SCRIPT_SRC)) {
    return
  }

  const first = scripts[0]
  const script = document.createElement('script')
  script.async = true
  script.src = `${SCRIPT_SRC}?id=${counterId}`
  if (first?.parentNode) {
    first.parentNode.insertBefore(script, first)
  } else {
    document.head.appendChild(script)
  }
}

function ensureYmQueue(): void {
  if (typeof window.ym === 'function') return
  const queueFn = ((...args: any[]) => {
    ;(queueFn.a = queueFn.a || []).push(args)
  }) as ((...args: any[]) => void) & { a?: any[]; l?: number }
  queueFn.l = Date.now()
  window.ym = queueFn
}

function injectNoScriptPixel(counterId: number): void {
  if (document.getElementById(NOSCRIPT_ID)) return

  const wrap = document.createElement('div')
  wrap.id = NOSCRIPT_ID
  wrap.style.cssText = 'position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;'

  const img = document.createElement('img')
  img.src = `https://mc.yandex.ru/watch/${counterId}`
  img.alt = ''
  img.style.cssText = 'position:absolute;left:-9999px;'

  wrap.appendChild(img)
  document.body.appendChild(wrap)
}

/** Load script + init counter + page view (safe to call on landing mount). */
export function initYandexMetrika(): void {
  const counterId = getYandexMetrikaId()
  if (!counterId || typeof document === 'undefined' || isTestHost()) return

  ensureYmQueue()
  injectLoaderScript(counterId)
  injectNoScriptPixel(counterId)

  window.ym?.(counterId, 'init', {
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    referrer: document.referrer,
    url: location.href,
    accurateTrackBounce: true,
    trackLinks: true,
  })
}

/** Custom conversion goal. */
export function trackYandexMetrikaGoal(goal: string, params?: Record<string, unknown>): void {
  const counterId = getYandexMetrikaId()
  const goalId = goal.trim()
  if (!counterId || !goalId || isTestHost()) return
  window.ym?.(counterId, 'reachGoal', goalId, params ?? {})
}
